import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import cn from 'classnames';

import { ibeSalonBusinessInfoAndHoursRead, ibeSalonBusinessInfoAndHoursUpdate } from 'actions/salon/beSalonBusinessInfoAndHours.action';
import { feSalonUpdate } from 'actions/ui.actions';
import ICO_HOUR from 'assets/images/menu/settings-menu/building-purple.svg';
import CheckboxInput from 'components/form/CheckboxInput.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import DELAY from 'constants/delay.const';
import IN from 'constants/in';
import useAsyncFnFix from 'hooks/useAsyncFnFix.hook';
import { usePrepareFetcher } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import C from 'utils/values/constant.util';

import SectionContent from 'settings/online-booking/pages/SectionContent.component';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';
import TimePicker from 'settings/salon/TimePicker.component';

import CN from './OperatingHours.module.scss';

const L = logger('OperatingHours'); // eslint-disable-line no-unused-vars

const fromMeridiemToHours = (raw) => {
  const value = fromAnyToTrimmed(raw);
  if ('' === value) return '';

  const [splitTime, splitMeridiem] = value.split(' ');
  const meridiem = splitMeridiem.toLowerCase();
  if ('am' !== meridiem && 'pm' !== meridiem) {
    console.warn('Invalid meridiem:', meridiem);
    return '';
  }

  const [splitHours, splitMinutes] = splitTime.split(':');
  const hours = fromAnyToInteger(splitHours);
  const isAm = 'am' === meridiem;
  const finalHours = 12 === hours ? (isAm ? 0 : 12) : isAm ? hours : hours + 12;
  const paddedHours = String(finalHours).padStart(2, '0');
  const paddedMinutes = String(fromAnyToInteger(splitMinutes)).padStart(2, '0');
  return `${paddedHours}:${paddedMinutes}:00`;
};

const DAYS_OF_WEEK = C([
  {
    tKeyDay: 'common.days.monday',
    tKeyDayAbbreviation: 'common.days.abbreviation.monday',
    keyDayOpen: 'monOpen',
    keyDayOpenTime: 'monOpenTime',
    keyDayCloseTime: 'monCloseTime',
    position: 'bottom',
  },
  {
    tKeyDay: 'common.days.tuesday',
    tKeyDayAbbreviation: 'common.days.abbreviation.tuesday',
    keyDayOpen: 'tueOpen',
    keyDayOpenTime: 'tueOpenTime',
    keyDayCloseTime: 'tueCloseTime',
    position: 'bottom',
  },
  {
    tKeyDay: 'common.days.wednesday',
    tKeyDayAbbreviation: 'common.days.abbreviation.wednesday',
    keyDayOpen: 'wedOpen',
    keyDayOpenTime: 'wedOpenTime',
    keyDayCloseTime: 'wedCloseTime',
    position: 'bottom',
  },
  {
    tKeyDay: 'common.days.thursday',
    tKeyDayAbbreviation: 'common.days.abbreviation.thursday',
    keyDayOpen: 'thuOpen',
    keyDayOpenTime: 'thuOpenTime',
    keyDayCloseTime: 'thuCloseTime',
    position: 'bottom',
  },
  {
    tKeyDay: 'common.days.friday',
    tKeyDayAbbreviation: 'common.days.abbreviation.friday',
    keyDayOpen: 'friOpen',
    keyDayOpenTime: 'friOpenTime',
    keyDayCloseTime: 'friCloseTime',
    position: 'top',
  },
  {
    tKeyDay: 'common.days.saturday',
    tKeyDayAbbreviation: 'common.days.abbreviation.saturday',
    keyDayOpen: 'satOpen',
    keyDayOpenTime: 'satOpenTime',
    keyDayCloseTime: 'satCloseTime',
    position: 'top',
  },
  {
    tKeyDay: 'common.days.sunday',
    tKeyDayAbbreviation: 'common.days.abbreviation.sunday',
    keyDayOpen: 'sunOpen',
    keyDayOpenTime: 'sunOpenTime',
    keyDayCloseTime: 'sunCloseTime',
    position: 'top',
  },
]);

// NOTE: map for convert keys from monCloseTime to mon_close_time to support old core api.
const OLD_KEYS = {
  monOpenTime: 'mon_open_time',
  monCloseTime: 'mon_close_time',
  tueOpenTime: 'tue_open_time',
  tueCloseTime: 'tue_close_time',
  wedOpenTime: 'wed_open_time',
  wedCloseTime: 'wed_close_time',
  thuOpenTime: 'thu_open_time',
  thuCloseTime: 'thu_close_time',
  friOpenTime: 'fri_open_time',
  friCloseTime: 'fri_close_time',
  satOpenTime: 'sat_open_time',
  satCloseTime: 'sat_close_time',
  sunOpenTime: 'sun_open_time',
  sunCloseTime: 'sun_close_time',
};

const HeaderHours = ({ salonHours }) => {
  const { t } = useTranslation();

  return (
    <div className={CN.flex}>
      <div className={CN.title}>{t('staffAndBusiness.businessInfoAndHours.openingHours.foldoutTitle')}</div>
      <div className={CN.daysContainer}>
        {DAYS_OF_WEEK.map((day) => (
          <div
            key={day.keyDayOpen}
            className={cn({
              [CN.day]: true,
              [CN.active]: salonHours?.[day.keyDayOpen],
            })}
          >
            {t(day.tKeyDayAbbreviation)}
          </div>
        ))}
      </div>
    </div>
  );
};

const OperatingHours = ({ feSalonUpdate, currentSalon }) => {
  const operatingHoursRef = useRef(null);
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const prepHoursRead = usePrepareFetcher(ibeSalonBusinessInfoAndHoursRead);
  const resultHoursRead = useQuery(prepHoursRead);

  const prepHourUpdate = usePrepareFetcher(ibeSalonBusinessInfoAndHoursUpdate);
  const resultHourUpdate = useMutation(prepHourUpdate);

  const [renderSavedIndicator, setSavedIndicator] = useState(null);

  const [resultDaysSave, handleCheckboxChange] = useAsyncFnFix(
    async (keyDayOpen) => {
      const currentValue = resultHoursRead?.data?.json?.[keyDayOpen];
      const updatedData = {
        [keyDayOpen]: !currentValue,
      };

      const result = await resultHourUpdate.mutateAsync({ data: updatedData });
      if (!result?.is?.ok) return;

      feSalonUpdate(updatedData);

      resultHoursRead.refetch();
    },
    [feSalonUpdate, resultHourUpdate, resultHoursRead],
  );

  const [resultTimeSave, handleTimeChange] = useAsyncFnFix(
    async (key, time) => {
      const formattedTime = fromMeridiemToHours(time);

      const updatedData = {
        [key]: formattedTime, // friCloseTime
      };

      const currentSalonKey = OLD_KEYS[key];
      if (currentSalonKey) {
        updatedData[currentSalonKey] = formattedTime;
      }

      const result = await resultHourUpdate.mutateAsync({ data: updatedData });
      if (!result?.is?.ok) return;

      if (result?.is?.ok) {
        setSavedIndicator(key);

        delayed(DELAY.autohide, () => setSavedIndicator(null));
      }
      feSalonUpdate(updatedData); // sending both: friCloseTime and fri_close_time
      await resultHoursRead.refetch();
    },
    [feSalonUpdate, resultHourUpdate, resultHoursRead],
  );

  if (resultHoursRead.isLoading) return <OverlaySpinner />;
  if (resultHoursRead.error) return <div>{t('staffAndBusiness.businessInfoAndHours.openingHours.loadingErrorMsg')}</div>;

  const salonHours = resultHoursRead?.data?.json;

  L.debug('()', {
    resultDaysSave,
    resultTimeSave,
    salonHours,
    currentSalon,
  });

  return (
    <div
      data-bem="OperatingHours"
      ref={operatingHoursRef}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <Foldout isOpen tag="OperatingHours__days" src={ICO_HOUR} width="md" title={<HeaderHours salonHours={salonHours} />}>
        <SectionSeparator />

        <div className={CN.foldoutHead}>{t('staffAndBusiness.businessInfoAndHours.openingHours.title')}</div>
        <SectionContent>
          {DAYS_OF_WEEK.map(({ position, tKeyDay, keyDayOpen, keyDayOpenTime, keyDayCloseTime }) => (
            <div
              key={keyDayOpen}
              data-bem="OperatingHours__day"
              className={cn({
                [CN.container]: true,
                [CN.unselected]: !salonHours?.[keyDayOpen],
              })}
            >
              <CheckboxInput variant="switch" checked={salonHours?.[keyDayOpen]} onClick={() => handleCheckboxChange(keyDayOpen)}>
                <div
                  className={cn({
                    [CN.dayName]: true,
                    [CN.unselected]: !salonHours?.[keyDayOpen],
                  })}
                >
                  {t(tKeyDay)}
                  {(renderSavedIndicator === keyDayOpenTime || renderSavedIndicator === keyDayCloseTime) && (
                    <div className={CN.savedIndicator}>
                      <Icon variant="confirm" width="md" /> <span className={CN.savedText}>{t('staffAndBusiness.businessInfoAndHours.openingHours.saved')}</span>
                    </div>
                  )}
                </div>
              </CheckboxInput>

              <TooltipForReceptionist where={IN.btnHours} />

              {salonHours?.[keyDayOpen] && (
                <div data-bem="OperatingHours__row--day" className={CN.row}>
                  <div data-bem="OperatingHours__day-open" className={CN.column}>
                    <TimePicker
                      position={position}
                      selectedTime={salonHours?.[keyDayOpenTime]}
                      onTimeSelect={(time) => handleTimeChange(keyDayOpenTime, time)}
                      name={t('staffAndBusiness.businessInfoAndHours.openingHours.opening')}
                    />
                  </div>
                  <div className={CN.divider}>—</div>
                  <div data-bem="OperatingHours__day-close" className={CN.column}>
                    <TimePicker
                      position={position}
                      selectedTime={salonHours?.[keyDayCloseTime]}
                      onTimeSelect={(time) => handleTimeChange(keyDayCloseTime, time)}
                      minTime={salonHours?.[keyDayOpenTime]}
                      name={t('staffAndBusiness.businessInfoAndHours.openingHours.closing')}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </SectionContent>
      </Foldout>
    </div>
  );
};

const OperatingHoursHoc = connect(
  (state) => ({
    currentSalon: state?.currentSalon,
  }),
  mapActionsToDispatchToProps({
    feSalonUpdate,
  }),
)(OperatingHours);

export default OperatingHoursHoc;
